import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, CheckCircle, User, Mail, Phone, MessageSquare } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplicationForm = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    role_interest: "both",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/applications`, formData);
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success("Application submitted successfully!", {
          description: "We'll be in touch soon.",
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to submit application", {
        description: "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="apply-section" className="py-24 md:py-32 relative bg-slate-50" data-testid="application-section">
        <div className="container-main relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="bg-white rounded-2xl p-12 border border-green-200 shadow-sm">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Application Received!</h2>
              <p className="text-slate-600 text-lg mb-6">
                Thank you for your interest in joining CityWorth. Our team will review your
                application and reach out soon.
              </p>
              <p className="text-slate-500">
                Application sent to: tshumway@cityworth.com
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="apply-section" className="py-24 md:py-32 relative bg-slate-50" data-testid="application-section">
      <div className="container-main relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-sm font-semibold tracking-widest uppercase text-green-600 mb-4 block">
            Join the Team
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-slate-900">
            Ready to{" "}
            <span className="gradient-text">Transform Your Career?</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Take the first step toward a career without cold calling, without open houses,
            and with real support from day one.
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="max-w-2xl mx-auto"
        >
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 md:p-10 border border-slate-200 shadow-sm">
            <div className="space-y-6">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    required
                    className="pl-12 py-6 bg-slate-50 border-slate-200 focus:border-red-500 rounded-xl text-slate-900 placeholder:text-slate-400"
                    data-testid="input-full-name"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="pl-12 py-6 bg-slate-50 border-slate-200 focus:border-red-500 rounded-xl text-slate-900 placeholder:text-slate-400"
                    data-testid="input-email"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    required
                    className="pl-12 py-6 bg-slate-50 border-slate-200 focus:border-red-500 rounded-xl text-slate-900 placeholder:text-slate-400"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {/* Role Interest */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  I'm Interested In *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: "real_estate", label: "Real Estate" },
                    { value: "mortgage", label: "Mortgage" },
                    { value: "both", label: "Both / Open" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, role_interest: option.value }))}
                      className={`py-3 px-4 rounded-xl font-medium transition-all ${
                        formData.role_interest === option.value
                          ? "bg-gradient-to-r from-red-500 to-blue-500 text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                      data-testid={`role-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tell Us About Yourself (Optional)
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Share your background, experience, and why you're interested in CityWorth..."
                    rows={4}
                    className="pl-12 py-4 bg-slate-50 border-slate-200 focus:border-red-500 rounded-xl text-slate-900 placeholder:text-slate-400 resize-none"
                    data-testid="input-message"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary py-6 rounded-xl text-lg font-semibold text-white"
                data-testid="submit-application-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>

              <p className="text-center text-slate-500 text-sm">
                By submitting, you agree to be contacted by the CityWorth team.
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default ApplicationForm;
